import { Suspense, lazy, useState, useCallback, useEffect, useRef } from "react";
import {
  Portal, Paper, Image, Loader, Text, Group, Button, Alert, Stack,
  Tabs, Center, ActionIcon,
} from "@mantine/core";
import { IconGripHorizontal, IconX } from "@tabler/icons-react";
import { useAppStore } from "../store/appStore";
import { useRecentStore, makeRecentId } from "../store/recentStore";
import { useValidation } from "../api/validate";
import { useDrag } from "../hooks/useDrag";
import type { DownloadFn } from "./ValidationCharts";

const ValidationCharts = lazy(
  () => import("./ValidationCharts").then(m => ({ default: m.ValidationCharts }))
);

function formatCoord(lon: number, lat: number): string {
  const lonDir = lon >= 0 ? "E" : "W";
  const latDir = lat >= 0 ? "N" : "S";
  return `${Math.abs(lat).toFixed(4)}° ${latDir}, ${Math.abs(lon).toFixed(4)}° ${lonDir}`;
}

function downloadBase64(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

function getInitialPos(windowWidth: number) {
  return {
    x: Math.max(0, (window.innerWidth - windowWidth) / 2),
    y: Math.max(20, (window.innerHeight - 700) / 2),
  };
}

export function ValidationModal() {
  const {
    validationOpen, closeValidation,
    selectedHindcast, lon, lat, radius, layer,
  } = useAppStore();

  const [activeTab, setActiveTab]   = useState<string>("interactive");
  const [downloadFn, setDownloadFn] = useState<DownloadFn | null>(null);

  const handleDownloadReady = useCallback((fn: DownloadFn) => {
    setDownloadFn(() => fn);
  }, []);

  const params =
    validationOpen && selectedHindcast && lon != null && lat != null
      ? { datasource: selectedHindcast, lon, lat, radius, mode: layer }
      : null;

  const { data, isLoading, isError, error } = useValidation(params);

  const addRecent = useRecentStore((s) => s.addEntry);
  useEffect(() => {
    if (!data || !selectedHindcast || lon == null || lat == null) return;
    const statsSource = data.plot_data.stats_all;
    addRecent({
      id: makeRecentId(selectedHindcast, layer, lon, lat, radius),
      hindcast: selectedHindcast,
      layer,
      lon,
      lat,
      radius,
      timestamp: Date.now(),
      stats: {
        bias: statsSource.bias,
        rmsd: statsSource.rmsd,
        si: statsSource.si,
        n: statsSource.n,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const title = data
    ? `${selectedHindcast} — ${formatCoord(data.lon, data.lat)} (${radius} km radius)`
    : isLoading ? "Validating the hindcast…"
    : selectedHindcast ?? "";

  function filenameBase() {
    return `${layer}_validation_${selectedHindcast
      ?.toLowerCase().replace(/\s+/g, "_")
    }_lon_${data!.lon.toFixed(4)}_lat_${data!.lat.toFixed(4)}_radius_${radius.toFixed(0)}km`;
  }

  async function handleDownload() {
    if (!data) return;
    if (activeTab === "interactive" && downloadFn) {
      await downloadFn(filenameBase());
    } else {
      downloadBase64(data.figure, filenameBase() + ".png");
    }
  }

  const windowWidth = Math.min(900, window.innerWidth - 40);
  const windowRef = useRef<HTMLDivElement>(null);
  const { pos, onMouseDown } = useDrag(getInitialPos(windowWidth), windowRef);

  if (!validationOpen) return null;

  return (
    <Portal>
      <Paper
        ref={windowRef}
        shadow="xl"
        radius="md"
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: windowWidth,
          maxWidth: "calc(100vw - 20px)",
          zIndex: 300,
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
          overflow: "hidden",
        }}
      >
        {/* Drag handle / title bar */}
        <Group
          px="md" py="xs" gap="sm"
          onMouseDown={onMouseDown}
          style={{
            cursor: "grab",
            borderBottom: "1px solid var(--mantine-color-gray-2)",
            flexShrink: 0,
            userSelect: "none",
            background: "var(--mantine-color-gray-0)",
            borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
          }}
        >
          <IconGripHorizontal size={16} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
          <Text fw={600} size="sm" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title}
          </Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={closeValidation}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", padding: "var(--mantine-spacing-md)", flex: 1 }}>
          {isLoading && (
            <Stack align="center" gap="md" py="xl">
              <Loader size="lg" />
              <Text size="sm" c="dimmed" ta="center">
                Downloading 30+ years of satellite altimeter and hindcast
                timeseries. This can take up to a minute…
              </Text>
            </Stack>
          )}

          {isError && (
            <Alert color="red" title="Validation failed">
              {(error as Error).message}
            </Alert>
          )}

          {data && (
            <Stack gap="xs">
              {data.tstart && (
                <Text size="xs" c="dimmed" ta="center">
                  {data.tstart.slice(0, 10)} — {data.tend.slice(0, 10)} · depth{" "}
                  {data.depth.toFixed(0)} m
                </Text>
              )}

              <Tabs value={activeTab} onChange={(v) => v != null && setActiveTab(v)}>
                <Tabs.List>
                  <Tabs.Tab value="interactive">Interactive</Tabs.Tab>
                  <Tabs.Tab value="static">Static PNG</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="interactive" pt="sm">
                  <Suspense fallback={<Center py="xl"><Loader size="sm" /></Center>}>
                    <ValidationCharts
                      pd={data.plot_data}
                      onDownloadReady={handleDownloadReady}
                    />
                  </Suspense>
                </Tabs.Panel>

                <Tabs.Panel value="static" pt="sm">
                  <Image
                    src={data.figure}
                    fit="contain"
                    maw={900}
                    mx="auto"
                    alt="Validation figure"
                  />
                </Tabs.Panel>
              </Tabs>
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" disabled={!data} onClick={handleDownload}>
              {activeTab === "interactive" ? "Download charts as PNG" : "Download PNG"}
            </Button>
            <Button onClick={closeValidation}>Close</Button>
          </Group>
        </div>
      </Paper>
    </Portal>
  );
}
